import { useActivateDeactivateUser } from 'api/services/userService';
import { useTranslation } from 'react-i18next';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { UserRecord } from 'types/userTypes';
import { ErrorType } from 'types/genericTypes';
import { useCallback } from 'react';
import { GenericDeleteModal } from 'components/ui/GenericDeleteModal';

interface ActivateDeactivateUserProps {
  open: boolean;
  onCancel: () => void;
  user?: UserRecord;
  selectedCompany?: string;
}

export const ActivateDeactivateUser = ({
  open,
  onCancel,
  user,
  selectedCompany,
}: ActivateDeactivateUserProps) => {
  const { t } = useTranslation();
  const {
    mutateAsync: activateDeactivateUser,
    isPending: isActivateDeactivateUserPending,
  } = useActivateDeactivateUser();
  const { openNotification } = useFloatNotification();

  const handleActivateDeactivateUser = useCallback(async () => {
    try {
      await activateDeactivateUser({
        companyId: selectedCompany || '',
        userCompanyRoleId: user?.id || '',
        payload: {
          active: !user?.active,
        },
      });

      openNotification(
        t('success'),
        t(
          'users-page.users-list.activate-deactivate-success-notification-message'
        ),
        'success'
      );
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t(
            'users-page.users-list.activate-deactivate-error-notification-message'
          ),
          'error'
        );
      }
    }
    onCancel();
  }, [
    onCancel,
    activateDeactivateUser,
    selectedCompany,
    user?.id,
    user?.active,
    openNotification,
    t,
  ]);

  return (
    <GenericDeleteModal
      open={open}
      onCancel={onCancel}
      onOk={handleActivateDeactivateUser}
      confirmLoading={isActivateDeactivateUserPending}
      bodyText={
        user?.active
          ? t(
              'users-page.users-list.activate-deactivate-modal.body-deactivation'
            )
          : t('users-page.users-list.activate-deactivate-modal.body-activation')
      }
    />
  );
};
